const TIMELINE_ROWS = [ 1, 2, 3, 4, 5 ] as const;
const ORDER_BOOK_ROWS = [ 1, 2, 3, 4, 5, 6 ] as const;
const ENGAGEMENT_ROWS = [ 1, 2, 3 ] as const;

function SkeletonBlock( { className }: { className: string } ) {
    return <div className={`skeleton rounded ${className}`} />;
}

export function CryptoEventSkeleton() {
    return (
        <div
            className="relative flex h-full min-h-0 flex-1 flex-col"
            role="status"
            aria-label="Loading crypto event"
            aria-busy="true"
            data-event-skeleton="crypto"
        >
            <div className="event-detail-scrollable flex h-full min-h-0 flex-1 flex-col overflow-y-auto scrollbar-hide">
                <div className="flex w-full items-center">
                    <SkeletonBlock className="mt-4 h-5 w-28" />
                    <div className="ml-auto pt-4">
                        <SkeletonBlock className="size-4 rounded-full" />
                    </div>
                </div>

                <div className="flex items-center gap-3 overflow-hidden pb-4 pt-3">
                    <SkeletonBlock className="size-12 shrink-0 rounded-lg" />
                    <div className="min-w-0 flex-1 space-y-2">
                        <SkeletonBlock className="h-5 w-full" />
                        <SkeletonBlock className="h-5 w-[66%]" />
                    </div>
                    <div className="shrink-0 self-start">
                        <SkeletonBlock className="h-9 w-20 rounded-md" />
                    </div>
                </div>

                <div className="mb-2 flex items-center">
                    <div className="text-t3">
                        <div className="min-w-0 flex-1 space-y-2">
                            <SkeletonBlock className="h-3 w-20" />
                            <SkeletonBlock className="h-5 w-16" />
                        </div>
                    </div>
                    <div className="mx-5 h-8 w-[1px] bg-stroke" />
                    <div>
                        <div className="mb-2 flex items-center gap-1">
                            <SkeletonBlock className="h-3 w-24" />
                            <SkeletonBlock className="h-3 w-12" />
                        </div>
                        <SkeletonBlock className="h-5 w-20" />
                    </div>
                </div>

                <div className="relative h-[237px] w-full shrink-0">
                    <SkeletonBlock className="size-full rounded-[10px]" />
                    <div className="absolute bottom-9 left-0 z-10 flex items-center gap-1 rounded-lg border border-stroke bg-surface-card p-1">
                        <SkeletonBlock className="size-7 rounded-md" />
                        <SkeletonBlock className="size-7 rounded-md" />
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-2">
                        <SkeletonBlock className="h-4 flex-1 rounded-full" />
                        <SkeletonBlock className="h-4 w-16 rounded-full" />
                    </div>
                </div>

                <div className="my-3 flex w-full items-center">
                    <div className="flex h-8 shrink-0 items-center gap-2 rounded-full bg-brand-button px-3">
                        <SkeletonBlock className="h-4 w-10" />
                        <SkeletonBlock className="size-4 rounded-full" />
                        <SkeletonBlock className="size-4 rounded-full" />
                        <SkeletonBlock className="size-4 rounded-full" />
                    </div>
                    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                        {TIMELINE_ROWS.map( row => (
                            <SkeletonBlock key={row} className="h-8 w-16 shrink-0 rounded-full" />
                        ) )}
                    </div>
                </div>

                <div className="h-12 rounded-xl border border-stroke bg-surface-card px-3">
                    <div className="flex h-12 items-center justify-between">
                        <SkeletonBlock className="h-5 w-36" />
                        <SkeletonBlock className="size-6 rounded-full" />
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-stroke bg-surface-card p-3">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                            <SkeletonBlock className="h-6 w-12" />
                            <SkeletonBlock className="h-6 w-10" />
                            <SkeletonBlock className="h-6 w-14" />
                        </div>
                        <div className="flex items-center gap-2">
                            <SkeletonBlock className="size-6 rounded-full" />
                            <SkeletonBlock className="size-6 rounded-full" />
                        </div>
                    </div>
                    <div className="mb-2 grid grid-cols-[minmax(40px,1fr)_44px_minmax(0,1fr)_minmax(0,1fr)] gap-2 px-2">
                        <SkeletonBlock className="h-4 w-10" />
                        <SkeletonBlock className="h-4 w-9" />
                        <SkeletonBlock className="h-4 w-12 justify-self-end" />
                        <SkeletonBlock className="h-4 w-12 justify-self-end" />
                    </div>
                    <div className="space-y-1">
                        {ORDER_BOOK_ROWS.map( row => (
                            <div
                                key={row}
                                className="grid h-7 grid-cols-[minmax(40px,1fr)_44px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2 px-2"
                            >
                                <SkeletonBlock className="h-4 w-10" />
                                <SkeletonBlock className="h-4 w-8 justify-self-end" />
                                <SkeletonBlock className="h-4 w-12 justify-self-end" />
                                <SkeletonBlock className="h-4 w-14 justify-self-end" />
                            </div>
                        ) )}
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-stroke bg-surface-card">
                    <div className="flex items-center justify-between border-b border-stroke px-3">
                        <div className="flex h-10 items-center gap-4">
                            <SkeletonBlock className="h-5 w-20" />
                            <SkeletonBlock className="h-5 w-24" />
                        </div>
                        <SkeletonBlock className="h-5 w-16" />
                    </div>
                    <div className="space-y-3 p-3">
                        <div className="grid grid-cols-3 gap-3">
                            <SkeletonBlock className="h-11 rounded-lg" />
                            <SkeletonBlock className="h-11 rounded-lg" />
                            <SkeletonBlock className="h-11 rounded-lg" />
                        </div>
                        <SkeletonBlock className="h-10 w-full rounded-lg" />
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-stroke bg-surface-card p-3">
                    <div className="flex h-6 items-center justify-between">
                        <SkeletonBlock className="h-5 w-16" />
                        <SkeletonBlock className="size-5 rounded-full" />
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-stroke bg-surface-card p-3">
                    <div className="mb-3 flex h-8 items-center gap-6">
                        <SkeletonBlock className="h-5 w-16" />
                        <SkeletonBlock className="h-5 w-16" />
                        <SkeletonBlock className="h-5 w-20" />
                    </div>
                    <div className="space-y-3">
                        {ENGAGEMENT_ROWS.map( row => (
                            <div key={row} className="flex items-center gap-3">
                                <SkeletonBlock className="size-8 shrink-0 rounded-full" />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <SkeletonBlock className="h-4 w-[72%]" />
                                    <SkeletonBlock className="h-3 w-[44%]" />
                                </div>
                            </div>
                        ) )}
                    </div>
                </div>
            </div>

            <div className="-mx-4 bg-surface-card">
                <div className="w-full border-t border-stroke bg-surface-card">
                    <div className="relative flex w-full items-center gap-3 px-4 py-3">
                        <SkeletonBlock className="h-12 flex-1 rounded-md" />
                        <SkeletonBlock className="h-12 flex-1 rounded-md" />
                    </div>
                </div>
            </div>
        </div>
    );
}
